/**
 * ============================================================================
 *  File        : frontend/src/components/AppShell.tsx
 *  Module      : components
 *  Purpose     : React 管理台框架 — RBAC-gated 側欄、語系切換、使用者狀態、toast host
 *  Author      : Phoenix Lai
 *  Created     : 2026-05-30
 *  Updated     : 2026-05-31
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 */

import {
  BarChart3,
  Database,
  FolderKanban,
  LayoutDashboard,
  LogOut,
  ShieldCheck,
  Users,
} from 'lucide-react'
import type { ComponentType, ReactElement } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { usePermissions } from '../hooks/usePermissions'
import { modules } from '../data/navigation'
import { cn } from '../lib/cn'
import { Toaster } from './ui/sonner'
import type { Locale, ModuleDefinition } from '../types'

interface AppShellProps {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string) => string
}

const iconMap: Record<string, ComponentType<{ size?: number; 'aria-hidden'?: boolean }>> = {
  BarChart3,
  Database,
  FolderKanban,
  LayoutDashboard,
  ShieldCheck,
  Users,
}

export function AppShell({ locale, setLocale, t }: AppShellProps): ReactElement {
  const auth = useAuth()
  const { can } = usePermissions()

  // 隱藏無權限模組；disabled 模組保留但 render 為不可點 + tooltip。
  const visibleModules = modules.filter(
    (module) => module.disabled || can(module.permissionKey),
  )

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand-block">
          <span className="brand-mark">EF</span>
          <div>
            <strong>{t('app.shortName')}</strong>
            <span>{t('app.name')}</span>
          </div>
        </div>
        <nav className="primary-nav" aria-label={t('app.name')}>
          {visibleModules.map((module) => (
            <NavItem key={module.id} module={module} t={t} />
          ))}
        </nav>
      </aside>

      <main className="main-surface">
        <header className="topbar">
          <div>
            <span className="eyebrow">{t('auth.session')}</span>
            <strong>{auth.userLabel || t('app.shortName')}</strong>
          </div>
          <div className="topbar-actions">
            <div className="segmented" role="group" aria-label="locale">
              {(['zh-TW', 'ja', 'en'] as const).map((item) => (
                <button
                  key={item}
                  className={locale === item ? 'selected' : ''}
                  type="button"
                  onClick={() => setLocale(item)}
                >
                  {item}
                </button>
              ))}
            </div>
            <button className="icon-button" type="button" onClick={() => void auth.logout()}>
              <LogOut aria-hidden="true" size={18} />
              <span>{t('auth.signOut')}</span>
            </button>
          </div>
        </header>
        <Outlet />
      </main>
      <Toaster />
    </div>
  )
}

function NavItem({
  module,
  t,
}: {
  module: ModuleDefinition
  t: (key: string) => string
}): ReactElement {
  const Icon = iconMap[module.icon] ?? LayoutDashboard

  if (module.disabled) {
    return (
      <span
        className="nav-item cursor-not-allowed opacity-50"
        aria-disabled="true"
        title={t(module.disabledTooltipKey ?? 'rbac.disabledTooltip')}
      >
        <Icon aria-hidden={true} size={20} />
        <span>{t(module.labelKey)}</span>
      </span>
    )
  }

  return (
    <NavLink
      className={({ isActive }) => cn('nav-item', isActive && 'active')}
      end={module.path === '/'}
      to={module.path}
    >
      <Icon aria-hidden={true} size={20} />
      <span>{t(module.labelKey)}</span>
    </NavLink>
  )
}
